import React, { useState, useRef, useEffect } from 'react';

const COUNTRIES = [
    { code: 'US', name: 'United States', flag: '🇺🇸' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦' },
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
    { code: 'PL', name: 'Poland', flag: '🇵🇱' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪' },
    { code: 'FR', name: 'France', flag: '🇫🇷' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹' },
    { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
    { code: 'BE', name: 'Belgium', flag: '🇧🇪' },
    { code: 'CH', name: 'Switzerland', flag: '🇨🇭' },
    { code: 'AT', name: 'Austria', flag: '🇦🇹' },
    { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
    { code: 'NO', name: 'Norway', flag: '🇳🇴' },
    { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
    { code: 'FI', name: 'Finland', flag: '🇫🇮' },
    { code: 'IE', name: 'Ireland', flag: '🇮🇪' },
    { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
    { code: 'GR', name: 'Greece', flag: '🇬🇷' },
    { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿' },
    { code: 'HU', name: 'Hungary', flag: '🇭🇺' },
    { code: 'RO', name: 'Romania', flag: '🇷🇴' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺' },
    { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵' },
    { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
    { code: 'CN', name: 'China', flag: '🇨🇳' },
    { code: 'IN', name: 'India', flag: '🇮🇳' },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
    { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
    { code: 'CL', name: 'Chile', flag: '🇨🇱' },
    { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
    { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪' },
    { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
    { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
];

export function CountryInput({
    value,
    onChange,
    required = false,
    placeholder = 'Select country...',
    isMobile = false
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const dropdownRef = useRef(null);

    // Find selected country
    const selectedCountry = COUNTRIES.find(c => c.name === value || c.code === value);

    // Filter countries based on search
    const filteredCountries = COUNTRIES.filter(country =>
        country.name.toLowerCase().includes(search.toLowerCase()) ||
        country.code.toLowerCase().includes(search.toLowerCase())
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearch('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (country) => {
        onChange(country.name);
        setIsOpen(false);
        setSearch('');
    };

    return (
        <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
            {/*
              Visually-hidden native input so the browser can enforce `required`
              validation on form submit. Not type="hidden" because hidden inputs
              are excluded from constraint validation.
            */}
            <input
                tabIndex={-1}
                aria-hidden="true"
                required={required}
                value={value || ''}
                onChange={() => {}}
                style={{
                    position: 'absolute',
                    opacity: 0,
                    width: 0,
                    height: 0,
                    pointerEvents: 'none',
                }}
            />

            {/* Main Input/Button */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={selectedCountry ? `Country: ${selectedCountry.name}` : placeholder}
                style={{
                    width: '100%',
                    padding: isMobile ? '14px 12px' : '12px',
                    border: '1px solid #d0d0d0',
                    background: '#ffffff',
                    fontSize: '14px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: isMobile ? '48px' : 'auto',
                    color: selectedCountry ? '#1a1a1a' : '#999',
                }}
            >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {selectedCountry ? (
                        <span>{selectedCountry.name}</span>
                    ) : (
                        placeholder
                    )}
                </span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                    {isOpen ? '▲' : '▼'}
                </span>
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div
                    role="listbox"
                    aria-label="Countries"
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '4px',
                        background: '#ffffff',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        zIndex: 1000,
                        maxHeight: '300px',
                        overflowY: 'auto',
                    }}
                >
                    {/* Search Input */}
                    <div style={{
                        padding: '8px',
                        borderBottom: '1px solid #e5e5e5',
                        position: 'sticky',
                        top: 0,
                        background: '#fff',
                        zIndex: 1,
                    }}>
                        <input
                            type="text"
                            placeholder="Search countries..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #d0d0d0',
                                borderRadius: '4px',
                                fontSize: '14px',
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>

                    {/* Country List */}
                    <div>
                        {filteredCountries.length > 0 ? (
                            filteredCountries.map(country => (
                                <button
                                    key={country.code}
                                    type="button"
                                    role="option"
                                    aria-selected={selectedCountry?.code === country.code}
                                    onClick={() => handleSelect(country)}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        border: 'none',
                                        background: selectedCountry?.code === country.code ? '#f0f0f0' : 'transparent',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        fontSize: '14px',
                                        transition: 'background 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (selectedCountry?.code !== country.code) {
                                            e.currentTarget.style.background = '#f9f9f9';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (selectedCountry?.code !== country.code) {
                                            e.currentTarget.style.background = 'transparent';
                                        }
                                    }}
                                >
                                    <span style={{ fontSize: '20px' }}>{country.flag}</span>
                                    <span style={{ color: '#1a1a1a' }}>{country.name}</span>
                                </button>
                            ))
                        ) : (
                            <div style={{
                                padding: '20px',
                                textAlign: 'center',
                                color: '#999',
                                fontSize: '14px',
                            }}>
                                No countries found
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
